import { useForm } from '@sequencehq/utils'
import { Validator } from '@sequencehq/validation'
import { useCallback, useState } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useNotifications } from 'lib/hooks/useNotifications'

const MAX_COUNTER_SIGNERS = 5

type CounterSigner = {
  name: string
  email: string
}

type UseQuoteSettingsFormFields = {
  isSignaturesEnabled: boolean
  isInternalSignerEnabled?: boolean
  counterSigners: CounterSigner[]
}

type UseQuoteSettingsForm = {
  fieldValues: UseQuoteSettingsFormFields
  onSubmit: (values: UseQuoteSettingsFormFields) => Promise<void>
}

const validateCounterSigners: Validator = value => {
  const signers = value as CounterSigner[]
  if (!signers?.length) return undefined

  // Check for duplicate emails
  const emails = signers.map(s => s.email.toLowerCase())
  const uniqueEmails = new Set(emails)
  if (uniqueEmails.size !== emails.length) {
    return 'Duplicate email addresses are not allowed'
  }

  // Check max limit
  if (signers.length > MAX_COUNTER_SIGNERS) {
    return `Maximum of ${MAX_COUNTER_SIGNERS} counter signers allowed`
  }

  // Validate each signer
  for (const signer of signers) {
    if (!signer.name) {
      return 'Name is required for all signers'
    }
    if (!signer.email) {
      return 'Email is required for all signers'
    }
    if (signer.email && !signer.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      return 'Invalid email address'
    }
  }

  return undefined
}

export const useQuoteSettingsForm = ({
  fieldValues,
  onSubmit
}: UseQuoteSettingsForm) => {
  const flags = useFlags()
  const [isValid, setIsValid] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const notifications = useNotifications()

  const { fields, queries, mutators } = useForm({
    value: fieldValues,
    showValidationErrors: !isValid,
    fieldConfiguration: [
      {
        property: 'isSignaturesEnabled',
        validation: []
      },
      {
        property: 'isInternalSignerEnabled',
        validation: []
      },
      {
        property: 'counterSigners',
        validation: [validateCounterSigners]
      }
    ],
    onValidationStateChange: setIsValid
  })

  const handleCounterSignersChange = useCallback(
    (newSigners: CounterSigner[]) => {
      mutators.updateFormData({ counterSigners: newSigners })
    },
    [mutators]
  )

  const handleAddSigner = useCallback(() => {
    const currentSigners = queries.formData.counterSigners || []
    mutators.updateFormData({
      counterSigners: [...currentSigners, { name: '', email: '' }]
    })
  }, [queries.formData.counterSigners, mutators])

  const handleRemoveSigner = useCallback(
    (index: number) => {
      const currentSigners = queries.formData.counterSigners || []
      if (currentSigners.length <= 1) {
        mutators.updateFormData({
          counterSigners: [{ name: '', email: '' }]
        })
        return
      }

      const signersBefore = index !== 0 ? currentSigners.slice(0, index) : []
      const signersAfter = currentSigners.slice(index + 1) || []
      mutators.updateFormData({
        counterSigners: [...signersBefore, ...signersAfter]
      })
    },
    [queries.formData.counterSigners, mutators]
  )

  const submit = async () => {
    if (!queries.isValid) {
      setIsValid(false)
      const validationError = queries.validationResults.find(
        error => error.fieldName === 'counterSigners'
      )
      if (validationError?.message) {
        notifications.displayNotification(validationError.message, {
          type: 'error',
          isClosable: true,
          duration: 6000
        })
      }
      return
    }

    setIsSubmitting(true)

    try {
      await onSubmit(queries.formData)
      notifications.displayNotification('Changes saved successfully', {
        type: 'success',
        duration: 3000
      })
    } catch (error) {
      notifications.displayNotification('Failed to save changes', {
        type: 'error',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        duration: 3000
      })
    }

    setIsSubmitting(false)
    setIsValid(true)
  }

  const formFields = {
    ...fields,
    counterSigners: {
      ...fields.counterSigners,
      value: queries.formData.counterSigners || [],
      onChange: handleCounterSignersChange,
      onAddNew: handleAddSigner,
      onRemove: handleRemoveSigner,
      deleteAvailable: (queries.formData.counterSigners || []).length > 1,
      visible:
        queries.formData.isInternalSignerEnabled &&
        queries.formData.isSignaturesEnabled
    },
    isInternalSignerEnabled: {
      ...fields.isInternalSignerEnabled,
      visible: queries.formData.isSignaturesEnabled && flags.useSigners
    }
  }

  return {
    fieldsConfig: formFields,
    submit,
    isDirty: queries.isDirty,
    isSubmitting
  }
}
